/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import cstmTable from '../../../../share/components/vue/PaginationTable';
import ffdModal from '../../../vue/FFDModal';
import session from '../../../../share/session';
import { PHASE, API_TYPE, SESSION_PROPERTY } from './constants';
import { convertUniqueCount, updateColumns, setCstmListItem } from './req05_common_logic';
import ApiService from "../../../../share/util/api/service";
import resource from '../../../../share/util/resourceMap';
import util from '../../../../share/util/utils';
import diagnoResource from '../../../../share/util/diagnose';
import { getMeasuredDataName, getCsvFileName } from '../../../../util/fileExport';
import handler from '../../../../share/util/apiResultValidator';
import config from '../../../../share/config';
import { createModelDisplaySpec, createModelDisplayNameCurrentLanguage } from '../../../../share/util/vehicle';
import { outputRegistrationNumber, createRegistrationNumber } from "../../../../util/vinInfoUtils";
import projectData from '../../../../share/components/vue/ProjectData';

export default {
  name: "requirement50005",
  data() {
    return {
      phase: PHASE.EXECUTE,
      // ダイアログフラグ
      modal_requirement_50005: false,
      isLoading: false,
      isDispose: true,
      sessionProperty: SESSION_PROPERTY.DEFAULT,
      systemId: '',
      tableItems: [],
      cstmListItem: [],
      selectedSystem: {},
      ffdData: [],
      readResponse: [],
      filteredData: [],
      headerTitle: '',
      dtcName: '',
      ffdSystem: '',
      storedDataNumber: '',
      measureId: null,
      emptyListFfdStatus: '',
      // データ保存画面のVIN/車両番号
      vin: null,
      // データ保存画面の車両
      vehicleName: null,
      // データ保存画面の登録番号表示フラグ
      hideRegistrationNumber: true,
      // データ保存画面の登録番号表示
      registrationNumber: '',
      // データ保存画面の機能名
      functionName: 'individual.individual_diagnosis_function_work_support',
      // データ保存画面のデータ名
      dataName: '',
      // データ保存画面のメモ
      memo: '',
      // データ保存画面の診断コメント
      diagnoseComment: '',
    };
  },
  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true }
  },

  components: {
    'cstm-table': cstmTable,
    'ffd-modal': ffdModal,
    'project-data': projectData,
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
      vehicleModelDisplayNameList: state => state.masterResources.vehicleModelDisplayNameList,
      language: state => state.systems.language,
      vehicleInfo: state => state.vehicle.vehicleInfo
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR:
          return "danger";
        case PHASE.EXECUTE:
        case PHASE.DISPLAY_DTC:
        case PHASE.DISPLAY_FFD:
        case PHASE.MEMO:
        case PHASE.RE_GET:
        default:
          return "secondary";
      }
    },

    /** モーダルサイズ */
    modalSize() {
      switch (this.phase) {
        case PHASE.DISPLAY_DTC:
        case PHASE.DISPLAY_FFD:
          return 'huge';
        case PHASE.MEMO:
          return 'lg';
        case PHASE.EXECUTE:
        case PHASE.ERROR:
        default:
          return 'md';
      }
    },

    /** ヘッダ表示文言 */
    modalHeaderTitle() {
      // DTC画面ヘッダタイトル
      const confirmationTitle = 'message.common_title_confirmation';
      // FFD画面ヘッダタイトル
      const ffdTitle = this.headerTitle;
      // 実行中画面ヘッダタイトル
      const executeTitle = 'individual.common_title_execution';
      // エラー画面ヘッダタイトル
      const errorTitle = 'message.common_title_error';
      // メモ画面ヘッダタイトル
      const memoTitle = 'message.header_vehicle_dtc_note_modal';
      // 再取得ヘッダタイトル
      const reGetTitle = 'message.common_title_confirmation';

      const titleMap = {
        execute: executeTitle,
        displayFfd: ffdTitle,
        displayDtc: confirmationTitle,
        error: errorTitle,
        memo: memoTitle,
        reGet: reGetTitle
      };

      if (this.phase === PHASE.DISPLAY_FFD) {
        return titleMap[this.phase];
      }
      return this.$t(titleMap[this.phase]);
    },

    /** 表示文言 */
    workSupportMessage() {
      const messageMap = {
        displayDtc: 'individual.label_req05_warning',
        error: 'individual.error_communication_error',
        reGet: 'individual.message_diag_code_confirm_get_information'
      };
      return this.$te(messageMap[this.phase]) ? this.$t(messageMap[this.phase]).split('<br/>').join('\n') : '';
    },

    /**  DTC一覧を表示するかどうか */
    showDTCTable() {
      const showDTCTableMap = {
        displayDtc: true
      };
      let result = showDTCTableMap[this.phase];
      if (typeof result === 'undefined') {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * 診断系のAPIは複数画面から使用されているため
     * 現在の画面で使用されるAPIが診断/データ再生/個別診断のうちどの機能なのか判別する
     * @returns {string} 使用元の機能
     */
    operationScreen() {
      return diagnoResource.operationFrom(this.$router.currentRoute.value.name);
    },
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.modal_requirement_50005 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.ffdSystem = this.$t(`diagSytem.${this.systemId}`);
      // プロジェクト保存画面での登録番号の表示有無を判定
      const vehicle = this.vehicleInfo.vehicle;
      if (vehicle.vin.type === config.VEHICLEINFO_TYPE_VIN) {
        this.hideRegistrationNumber = false;
      }
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_50005 = false;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** POST, GET API 実行 */
    async executeApi(apiType) {
      const token = session.getToken();
      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        this.systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: apiType
        },
        // エラー発生のコールバックを実装する。
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列、もしくは独自定義した文字列が返ってくる。
        // 必要に応じて、errorType に応じてメッセージを変えるといったことも可能。
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );
      return response;
    },

    /** 処理 */
    async process() {
      try {
        this.isLoading = true;
        if (!await this.start()) {
          this.showUnexpectedError();
          return;
        }
        if (!await this.read()) {
          this.showUnexpectedError();
          return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** DTC再取得 */
    async reGetRead() {
      try {
        this.isLoading = true;
        if (!await this.read()) {
          this.showUnexpectedError();
          return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** NonDefaultSession遷移 */
    async start() {
      const response = await this.executeApi(API_TYPE.STANDBY);

      // responseが存在しない場合、共通エラーに遷移
      if (!response) {
        return false;
      }

      switch (response.data.status) {
        case REQUEST_STATUS_COMPLETED:
          this.sessionProperty = SESSION_PROPERTY.NON_DEFAULT;
          return true;
        case REQUEST_STATUS_FAILED:
        default:
          // 共通エラー
          return false;
      }
    },

    /** PredictiveSSR取得 */
    async read() {
      const response = await this.executeApi(API_TYPE.READ);

      // responseが存在しない場合、共通エラーに遷移
      if (!response) {
        return false;
      }
      switch (response.data.status) {
        case REQUEST_STATUS_COMPLETED:
          // DTC表示画面
          this.phase = PHASE.DISPLAY_DTC;
          this.measureId = String(response.data.user_data.measure_id);
          this.readResponse = response.data.user_data.read_data_list;
          // DTCコードがあるもののみ表示する
          this.filteredData = this.readResponse.filter(d => d.code !== null);
          // DTC無しリスト且つ、FFD状態が存在する場合、FFD状態を保持する
          if (this.filteredData.length <= 0){
            if (this.readResponse.find(item => item.ffd_status === 'ng')) {
              this.emptyListFfdStatus = 'ng';
            } else if (this.readResponse.find(item => item.ffd_status === 'no')) {
              this.emptyListFfdStatus = 'no';
            }
          } 
          updateColumns(this);
          setCstmListItem(this);

          return true;
        case REQUEST_STATUS_FAILED:
        default:
          // 共通エラー
          return false;
      }
    },

    /** DefaultSession遷移 */
    async terminate() {
      const response = await this.executeApi(API_TYPE.TERMINATE);

      // responseが存在しない場合、共通エラーに遷移
      if (!response) {
        return false;
      }

      switch (response.data.status) {
        case REQUEST_STATUS_COMPLETED:
          this.sessionProperty = SESSION_PROPERTY.DEFAULT;
          return true;
        case REQUEST_STATUS_FAILED:
        default:
          // 共通エラー
          return false;
      }
    },

    /** Dispose */
    async dispose() {
      this.isDispose = false;
      // 実行結果にかかわらず終了
      await this.executeApi(API_TYPE.DISPOSE);
    },

    /** DTC表示画面テーブル情報設定 */
    updateTableItems() {
      if (this.filteredData.length > 0) {
        return this.filteredData.map(e => ({
          dtc: e.code,
          description: resource.getDtcDescriptionResource(e, this),
          tripCount: util.isNullOrDefault(e.igon_time, '-'),
          timeCount: util.isNullOrDefault(e.igon_mill_sec, '-'),
          uniqueCount: convertUniqueCount(e.unique_count, this),
          buttonVisible: e.has_ffd,
          isMultiFFD: e.is_multi_ffd,
          ffd_data: e.data,
          systemID: e.system,
          ffd_status: e.ffd_status,
          ffd_status_name: e.ffd_status === 'ng' ? 'individual.label_status_acquisition_error' : e.ffd_status === 'no' ? 'individual.label_status_no_data' : '-',
          stored_data_number: e.stored_data_number ? e.stored_data_number : '-',
        }));
      } else {
        // データがない時、全て「-」
        return [{
          dtc: this.$t('message.common_hyphen'),
          description: 'message.common_hyphen',
          tripCount: this.$t('message.common_hyphen'),
          timeCount: this.$t('message.common_hyphen'),
          uniqueCount: this.$t('message.common_hyphen'),
          buttonVisible: false,
          ffd_status: this.emptyListFfdStatus,
          ffd_status_name: this.emptyListFfdStatus === 'ng' ? 'individual.label_status_acquisition_error' : this.emptyListFfdStatus === 'no' ? 'individual.label_status_no_data' : '-',
          stored_data_number: '-',
        }];
      }
    },

    /**
     * メモダイアログを表示
     */
    async openNoteModal() {
      try {
        this.isLoading = true;
        /** メモ情報のクリア */
        this.diagnoseComment = this.memo = this.dataName = '';

        // 計測データ取得APIを指定
        const endPoint = '/projects/' + this.vehicleInfo.vehicle.project.id + '/' + this.measureId;
        // 計測データ名を取得
        const res = await ApiService.getCall(ApiService.WSP, endPoint, this.operationScreen);
        const result = handler.validate(handler.validateTypes.all, res, this, null, () => {
          // データ名取得
          if (res.data && res.data.project) {
            const vcl = res.data.project;
            // プロジェクト保存画面に必要な情報を設定
            this.dataName = vcl.measured?.data_name;
            this.memo = vcl.measured?.memo;
            this.diagnoseComment = vcl.comment;
            const vehicle = this.vehicleInfo.vehicle;
            this.vin = vehicle.vin?.vin;
            // 車種情報から表示名称を取得
            const spec = createModelDisplaySpec(vehicle.vin.type, vehicle.spec);
            this.vehicleName = createModelDisplayNameCurrentLanguage(vcl.vin.vehicle_model, this.vehicleModelDisplayNameList, this.language, '{0} {1}', spec);
            // 海外VINの場合、登録番号は画面から非表示にする
            if (this.hideRegistrationNumber) {
              if (outputRegistrationNumber(vehicle.vin)) {
                this.registrationNumber = createRegistrationNumber(vehicle.vin);
              }
              else {
                this.registrationNumber = "";
              }
            }
            return true;
          } else {
            this.showUnexpectedError();
            return false;
          }
        }, (result) => {
          // レスポンス不正時のコールバック
          if (result.behaviorType === handler.behaviorTypes.logout) {
            this.logout(this);
            return false;
          }
          this.showUnexpectedError();
          return false;
        }, null, false);

        this.$nextTick(() => { 
          if (result) {
            this.phase = PHASE.MEMO;
          }
        });
      } finally {
        this.isLoading = false;
      }
    },

    /** ユーザ操作 OK押下 */
    onOkClicked() {
      switch (this.phase) {
        case PHASE.DISPLAY_DTC:
          this.onDisplayDtcOkClicked();
          break;
        case PHASE.DISPLAY_FFD:
          this.onDisplayFfdOkClicked();
          break;
        case PHASE.ERROR:
          this.clickErrorOk();
          break;
        case PHASE.RE_GET:
          this.reGetRead();
          break;
        default:
          break;
      }
    },

    /**
     * メモ情報の保存
     */
    async memoSave() {
      // バリデーションチェック
      if (this.$refs.projectData.validate()) {
        // 保存処理
        const ret = await this.update();
        if (ret) {
          // モーダル閉じ
          this.backDTC();
        }
      }
    },

    reGetDtc() {
      this.phase = PHASE.RE_GET;
    },

    /**
     * メモ情報のクリア
     */
    memoClear: function () {
      this.diagnoseComment = '';
      this.dataName = '';
      this.memo = '';
      this.$refs.projectData.clear();
    },

    /** DTC表示画面に戻る処理 */
    backDTC() {
      this.phase = PHASE.DISPLAY_DTC;
      this.$nextTick(() => {
        this.cstmListItem = this.updateTableItems();
      });
    },

    /** DTC表示画面 OK押下処理 */
    async onDisplayDtcOkClicked() {
      try {
        this.isLoading = true;
        if (!await this.terminate()) {
          this.showUnexpectedError();
          return;
        }
        await this.dispose();
        // 画面を閉じる
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /** エラー画面のOKクリック */
    async clickErrorOk() {
      try {
        this.isLoading = true;
        if (this.sessionProperty === SESSION_PROPERTY.NON_DEFAULT) {
          await this.terminate();
        }
        if (this.isDispose) {
          await this.dispose();
        }
        // 画面を閉じる
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /** FFD表示画面にてOK押下時の処理 */
    onDisplayFfdOkClicked() {
      // DTC表示画面に遷移
      this.phase = PHASE.DISPLAY_DTC;
      setCstmListItem(this);
    },

    /** FFD押下時の処理 */
    clickFFD(data) {
      if (!data) return;

      this.headerTitle = data.dtc;
      this.dtcName = this.$t(data.description);

      this.ffdData = data.ffd_data;
      this.storedDataNumber = data.stored_data_number;
      this.selectedSystem = data;

      this.phase = PHASE.DISPLAY_FFD;
    },

    /**
     * DTC情報をCSVエクスポートする
     */
    async csvExport() {
      const fileInfo = {};
      let resultInfo = {};
      // 行のフォーマット
      resultInfo = this.cstmListItem.map(item => {
        return {
          ...item,
          description: this.$t(item.description),
          ffd: item.buttonVisible ? this.$t('message.available_ffd') : item.ffd_status_name !== '-' ? this.$t(item.ffd_status_name) : this.$t('message.no_ffd')
        };
      });

      // 列のフォーマット
      fileInfo.column = this.tableItems.map(column => {
        return column;
      });

      // VINデータのフォーマット
      const vinHeader = `"${this.$t('message.label_measured_data_search_vin')}","${this.vehicleInfo.vehicle.vin.vin}"\n`;

      // 出力形式の設定
      const headers = fileInfo.column.map(column => {
        return { id: column.name, title: this.$t(column.label) };
      });

      // 説明・故障部位のエスケープ処理
      fileInfo.record = util.escapeDoubleQuotation(resultInfo, 'description');

      // CSVファイル名を設定
      let endPoint = '';
      let measuredDataName = '';

      if (this.vehicleInfo.vehicle.project.id !== null && this.measureId !== null) {
        try {
          this.isLoading = true;
          // プロジェクトIDおよび計測データIDが存在する場合、計測データ取得APIを指定
          endPoint = '/projects/' + this.vehicleInfo.vehicle.project.id + '/' + this.measureId;
          // 計測データ名を取得
          measuredDataName = await getMeasuredDataName(this, ApiService.WSP, endPoint, this.operationScreen);
        } finally {
          this.isLoading = false;
        }
      }
      const fileName = getCsvFileName(this.$t('individual.individual_diagnosis_function_diag_code'), measuredDataName, this.vehicleInfo.vehicle.vin.vin);

      // CSVファイルエクスポート処理
      const data = vinHeader + util.createCsvData(headers, fileInfo.record);

      // CSVファイルエクスポート処理
      util.csvFileExport(data, fileName);
    },

    /** Projectデータ更新処理 */
    async update() {
      try {
        this.isLoading = true;

        const endPoint = '/projects/' + this.vehicleInfo.vehicle.project.id + '/' + this.measureId;
        const reqBody = {
          name: this.vehicleInfo.vehicle.project.id,
          comment: this.$refs.projectData.inputDiagnoseComment,
          measured: {
            name: this.$refs.projectData.inputDataName,
            memo: this.$refs.projectData.inputMemo
          }
        };
        const res = await ApiService.postCall(ApiService.WSP, endPoint, reqBody, this.operationScreen);
        let ret = false;
        // APIレスポンスの検証
        handler.validate(handler.validateTypes.all, res, this, null, () => {
          ret = true;
        }, (result) => {
          // レスポンス不正時のコールバック
          if (result.behaviorType === handler.behaviorTypes.logout) {
            this.logout(this);
            return false;
          }
          this.showUnexpectedError();
          return false;
        }, null, false);
        return ret;
      } finally {
        this.isLoading = false;
      }
    },
  },

  async mounted() {
    this.init();
    this.process();
  }
};
